import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '@payment-plan/ui';
import { useCreateAdmin } from './use-admins';
import { advisorSchema } from '../advisors/advisor.schema';

// Mismos campos y reglas que el asesor, sin proyectos (el admin ve toda su inmobiliaria).
export const adminSchema = advisorSchema.omit({ proyectoIds: true });
export type AdminFormValues = z.infer<typeof adminSchema>;

const VACIO: AdminFormValues = { nombre: '', documento: '', correo: '', celular: '' };

export function AdminFormDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (nombre: string) => void;
}): React.JSX.Element {
  const crear = useCreateAdmin();
  const [serverError, setServerError] = React.useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AdminFormValues>({ resolver: zodResolver(adminSchema), defaultValues: VACIO });

  React.useEffect(() => {
    if (open) {
      setServerError(null);
      reset(VACIO);
    }
  }, [open, reset]);

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await crear.mutateAsync(values);
      onCreated(values.nombre);
      onOpenChange(false);
    } catch (error) {
      setServerError(error instanceof Error ? error.message : 'No fue posible crear el administrador.');
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Nuevo administrador</DialogTitle>
          <DialogDescription>
            Recibirá un correo con un enlace para definir su contraseña. Tendrá acceso de consulta a su inmobiliaria.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => void onSubmit(event)} noValidate className="space-y-4">
          <FormField label="Nombre" htmlFor="admin-nombre" required error={errors.nombre?.message}>
            <Input id="admin-nombre" error={Boolean(errors.nombre)} {...register('nombre')} />
          </FormField>
          <FormField label="Documento" htmlFor="admin-documento" required error={errors.documento?.message}>
            <Input id="admin-documento" error={Boolean(errors.documento)} {...register('documento')} />
          </FormField>
          <FormField label="Correo" htmlFor="admin-correo" required error={errors.correo?.message}>
            <Input id="admin-correo" type="email" error={Boolean(errors.correo)} {...register('correo')} />
          </FormField>
          <FormField label="Celular" htmlFor="admin-celular" required error={errors.celular?.message}>
            <Input id="admin-celular" error={Boolean(errors.celular)} {...register('celular')} />
          </FormField>
          {serverError && (
            <p role="alert" className="text-sm text-destructive">
              {serverError}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Enviar invitación
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
